import type { QueryClient } from '@tanstack/react-query';
import type { ProductConfId, SetProductConf } from '../api/proform/proform-types';

export type ConfiguratorMutationContext = {
  cislo_nomenklatury: string;
  id_shop: number;
  user_konf_id: string | number;
  e_mail: string;
};

export type ConfiguratorFillingSelection = {
  column: 'user_grp_skla' | 'user_kod_skla';
  value: string;
};

export class ConfiguratorFillingSelectionRejectedError extends Error {
  constructor() {
    super('The server did not retain the requested filling selection.');
    this.name = 'ConfiguratorFillingSelectionRejectedError';
  }
}

export function getConfiguratorMutationScope(context: ConfiguratorMutationContext): string {
  // The initial id changes from 1 after the first write; the scope must stay stable.
  return JSON.stringify([
    'SET_PRODUCT_CONF',
    context.e_mail,
    context.id_shop,
    context.cislo_nomenklatury,
  ]);
}

export function getConfiguratorIdQueryKey(context: ConfiguratorMutationContext) {
  return [
    'configurator',
    'GET_PRODUCT_CONF_ID',
    context.e_mail,
    {
      user_konf_id: context.user_konf_id,
      cislo_nomenklatury: context.cislo_nomenklatury,
      session_e_mail: context.e_mail,
      id_shop: context.id_shop,
    },
  ] as const;
}

/** Keep a successful SET pending until its authoritative, exact configuration GET finishes. */
export async function synchronizeConfiguratorMutation({
  queryClient,
  context,
  mutate,
  fetchConfiguration,
  setConfigurationId,
  fillingSelection,
}: {
  queryClient: QueryClient;
  context: ConfiguratorMutationContext;
  mutate: () => Promise<SetProductConf>;
  fetchConfiguration: (context: ConfiguratorMutationContext) => Promise<ProductConfId>;
  setConfigurationId: (id: string | number) => void;
  fillingSelection?: ConfiguratorFillingSelection;
}): Promise<SetProductConf> {
  await queryClient.cancelQueries({ queryKey: getConfiguratorIdQueryKey(context), exact: true });
  const response = await mutate();
  if (!response?.root || response.root.status === 'error') {
    throw new Error('Konfiguráciu sa nepodarilo uložiť.');
  }

  const configurationId = response.root.user_konf_id;
  if (configurationId === undefined || configurationId === null || configurationId === '') {
    throw new Error('Odpoveď konfigurátora neobsahuje identifikátor konfigurácie.');
  }

  const hasNewConfigurationId = String(configurationId) !== String(context.user_konf_id);
  // Query keys distinguish 42 from '42'. Keep the existing representation when the
  // server returns the same logical id so the mounted field observes the refreshed GET.
  const savedContext = {
    ...context,
    user_konf_id: hasNewConfigurationId ? configurationId : context.user_konf_id,
  };
  const queryKey = getConfiguratorIdQueryKey(savedContext);
  // Retain the server-created id even if the follow-up GET fails. A queued write must not
  // create another configuration from the original id 1.
  if (hasNewConfigurationId) {
    setConfigurationId(configurationId);
  }

  // Cancel any focus/refetch request started during SET before reading its final state.
  await queryClient.cancelQueries({ queryKey, exact: true });
  const configuration = await queryClient.fetchQuery({
    queryKey,
    queryFn: async () => {
      const configuration = await fetchConfiguration(savedContext);
      if (!configuration?.root?.smako_conf?.column) {
        throw new Error('Aktuálnu konfiguráciu sa nepodarilo načítať.');
      }
      return configuration;
    },
    staleTime: 0,
  });
  if (fillingSelection) {
    const actual = configuration.root.smako_conf.column[fillingSelection.column];
    const normalize = (value: string | null | undefined) =>
      String(value ?? '')
        .trim()
        .toUpperCase();
    if (normalize(actual) !== normalize(fillingSelection.value)) {
      // The authoritative GET stays in cache, including server-cleared dependent fields.
      // Report rejection instead of inventing a local selection the server did not accept.
      throw new ConfiguratorFillingSelectionRejectedError();
    }
  }
  return response;
}
